/**
 * Справочник приложений для страницы «Подключиться».
 *
 * Единый источник: платформы → приложения → ссылки на установку, схема deep-link
 * «открыть в приложении» и оговорки. Тот же набор продублирован в боте
 * (backend/internal/adapter/telegram/guide.go) — менять синхронно.
 *
 * Кого рекомендуем и почему:
 * - Happ первым на всех платформах: единственный клиент, в который панель отдаёт
 *   наш роутинг (банки, MAX, госуслуги — напрямую, минуя VPN).
 * - Hiddify не предлагаем: он на ядре sing-box, с нашими нодами работает через раз
 *   и наш роутинг не получает.
 * - v2RayTun: пинг в нём строже остальных, при «подключено, но не грузится»
 *   помогает фрагментация в настройках приложения.
 * - RabbitHole и FlClashX (ядро Mihomo): панель сама отдаёт им свой формат по обычной ссылке.
 */

export type PlatformId = 'android' | 'ios' | 'pc' | 'tv';

export interface DownloadLink {
  label: string;
  url: string;
}

export interface GuideApp {
  id: string;
  name: string;
  /** Подпись под названием; есть только у рекомендуемого приложения */
  tagline?: string;
  recommended?: boolean;
  downloads: DownloadLink[];
  /** Схема «открыть в приложении»; нет — только копирование ссылки */
  deepLink?: (subscriptionUrl: string) => string;
  /** Как добавить подписку руками, если deep-link не сработал */
  addHint: string;
  /** Оговорка, которая показывается в шаге «Включите VPN» */
  note?: string;
}

export interface GuidePlatform {
  id: PlatformId;
  label: string;
  /** Подпись в шаге «Скачайте …» */
  downloadFor: string;
  apps: GuideApp[];
}

const HAPP_TAGLINE = 'Рекомендуем, простой и стабильный';
const HAPP_ADD_HINT = 'В Happ: «+» в правом верхнем углу → «Вставить из буфера обмена»';

const happDeepLink = (url: string) => `happ://add/${url}`;
const v2raytunDeepLink = (url: string) => `v2raytun://import/${url}`;
const incyDeepLink = (url: string) => `incy://add/${url}`;
const flclashxDeepLink = (url: string) =>
  `flclashx://install-config?url=${encodeURIComponent(url)}`;

const V2RAYTUN_NOTE =
  'Если подключение есть, а сайты не открываются — включите «Фрагментация» в настройках v2RayTun и переподключитесь.';

export const GUIDE_PLATFORMS: GuidePlatform[] = [
  {
    id: 'android',
    label: 'Android',
    downloadFor: 'Для Android',
    apps: [
      {
        id: 'happ',
        name: 'Happ',
        tagline: HAPP_TAGLINE,
        recommended: true,
        downloads: [
          {
            label: 'Google Play',
            url: 'https://play.google.com/store/apps/details?id=com.happproxy',
          },
          {
            label: 'APK',
            url: 'https://github.com/Happ-proxy/happ-android/releases/latest/download/Happ.apk',
          },
        ],
        deepLink: happDeepLink,
        addHint: HAPP_ADD_HINT,
      },
      {
        id: 'v2raytun',
        name: 'v2RayTun',
        downloads: [
          {
            label: 'Google Play',
            url: 'https://play.google.com/store/apps/details?id=com.v2raytun.android',
          },
        ],
        deepLink: v2raytunDeepLink,
        addHint: 'В v2RayTun: «+» → «Импорт из буфера обмена»',
        note: V2RAYTUN_NOTE,
      },
      {
        id: 'incy',
        name: 'INCY',
        downloads: [
          {
            label: 'APK (GitHub)',
            url: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/Incy.apk',
          },
        ],
        deepLink: incyDeepLink,
        addHint: 'В INCY: «+» → «Добавить подписку» → вставить ссылку',
      },
      {
        id: 'flclashx',
        name: 'FlClashX',
        downloads: [
          {
            label: 'APK (GitHub)',
            url: 'https://github.com/pluralplay/FlClashX/releases/latest/download/FlClashX-android-universal.apk',
          },
        ],
        deepLink: flclashxDeepLink,
        addHint: 'В FlClashX: «Профили» → «+» → «Добавить по URL» → вставить ссылку',
      },
    ],
  },
  {
    id: 'ios',
    label: 'iPhone / iPad',
    downloadFor: 'Для iPhone и iPad',
    apps: [
      {
        id: 'happ',
        name: 'Happ',
        tagline: HAPP_TAGLINE,
        recommended: true,
        downloads: [
          {
            label: 'App Store',
            url: 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215',
          },
        ],
        deepLink: happDeepLink,
        addHint: HAPP_ADD_HINT,
      },
      {
        id: 'v2raytun',
        name: 'v2RayTun',
        downloads: [
          {
            label: 'App Store',
            url: 'https://apps.apple.com/app/id6476628951',
          },
        ],
        deepLink: v2raytunDeepLink,
        addHint: 'В v2RayTun: «+» → «Импорт из буфера обмена»',
        note: V2RAYTUN_NOTE,
      },
      {
        id: 'incy',
        name: 'INCY',
        downloads: [
          {
            label: 'App Store',
            url: 'https://apps.apple.com/app/incy/id6756943388',
          },
        ],
        deepLink: incyDeepLink,
        addHint: 'В INCY: «+» → «Добавить подписку» → вставить ссылку',
      },
      {
        id: 'rabbithole',
        name: 'RabbitHole',
        downloads: [
          {
            label: 'App Store',
            url: 'https://apps.apple.com/app/rabbithole-vpn-client/id6683309629',
          },
        ],
        addHint: 'В RabbitHole: «+» → «Добавить подписку» → вставить ссылку',
      },
    ],
  },
  {
    id: 'pc',
    label: 'Компьютер',
    downloadFor: 'Для Windows, macOS и Linux',
    apps: [
      {
        id: 'happ',
        name: 'Happ',
        tagline: HAPP_TAGLINE,
        recommended: true,
        downloads: [
          {
            label: 'Windows',
            url: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/setup-Happ.x64.exe',
          },
          {
            label: 'macOS',
            url: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/Happ.macOS.universal.dmg',
          },
          {
            label: 'Linux (.deb)',
            url: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/Happ.linux.x64.deb',
          },
        ],
        deepLink: happDeepLink,
        addHint: HAPP_ADD_HINT,
      },
      {
        id: 'flclashx',
        name: 'FlClashX',
        downloads: [
          {
            label: 'Windows',
            url: 'https://github.com/pluralplay/FlClashX/releases/latest/download/FlClashX-windows-amd64-setup.exe',
          },
          {
            label: 'macOS (Apple Silicon)',
            url: 'https://github.com/pluralplay/FlClashX/releases/latest/download/FlClashX-macos-arm64.dmg',
          },
          {
            label: 'macOS (Intel)',
            url: 'https://github.com/pluralplay/FlClashX/releases/latest/download/FlClashX-macos-amd64.dmg',
          },
          {
            label: 'Linux (.deb)',
            url: 'https://github.com/pluralplay/FlClashX/releases/latest/download/FlClashX-linux-amd64.deb',
          },
        ],
        deepLink: flclashxDeepLink,
        addHint: 'В FlClashX: «Профили» → «+» → «Добавить по URL» → вставить ссылку',
      },
      {
        id: 'v2raytun',
        name: 'v2RayTun',
        downloads: [
          {
            label: 'Windows',
            url: 'https://github.com/DigneZzZ/v2raytun/releases/latest/download/v2RayTun_Setup.exe',
          },
        ],
        deepLink: v2raytunDeepLink,
        addHint: 'В v2RayTun: «+» → «Импорт из буфера обмена»',
        note: V2RAYTUN_NOTE,
      },
      {
        id: 'incy',
        name: 'INCY',
        downloads: [
          {
            label: 'Windows',
            url: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/incy-windows-setup.exe',
          },
          {
            label: 'macOS (Apple Silicon)',
            url: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/incy-macos-arm64.dmg',
          },
          {
            label: 'macOS (Intel)',
            url: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/incy-macos-intel.dmg',
          },
          {
            label: 'Linux (.deb)',
            url: 'https://github.com/INCY-DEV/incy-platforms/releases/latest/download/incy-linux-x64.deb',
          },
        ],
        deepLink: incyDeepLink,
        addHint: 'В INCY: «+» → «Добавить подписку» → вставить ссылку',
      },
    ],
  },
  {
    id: 'tv',
    label: 'TV',
    downloadFor: 'Для Android TV и Apple TV',
    apps: [
      {
        id: 'happ',
        name: 'Happ',
        tagline: HAPP_TAGLINE,
        recommended: true,
        downloads: [
          {
            label: 'Android TV (Google Play)',
            url: 'https://play.google.com/store/apps/details?id=com.happproxy',
          },
          {
            label: 'Apple TV (App Store)',
            url: 'https://apps.apple.com/us/app/happ-proxy-utility-for-tv/id6748297274',
          },
        ],
        addHint: 'На телевизоре проще всего отсканировать QR-код ниже камерой в приложении Happ',
      },
    ],
  },
];

/** Платформа по user-agent: открываем вкладку того устройства, с которого зашли */
export function detectPlatform(userAgent: string): PlatformId {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return /\btv\b|smart-tv|aft[a-z]/.test(ua) ? 'tv' : 'android';
  // iPadOS 13+ в Safari представляется как Mac — ловим по тач-экрану
  if (/macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
    return 'ios';
  }
  return 'pc';
}

/** Короткие ответы на частые проблемы, показываются под инструкцией */
export const TROUBLESHOOTING: { title: string; text: string }[] = [
  {
    title: 'Подключено, но сайты не открываются',
    text: 'Выберите другой сервер из списка, обновите подписку в приложении и переподключитесь. Если не помогло — выключите и включите VPN ещё раз.',
  },
  {
    title: 'Не работает приложение банка, MAX или Госуслуги',
    text: 'В Happ они идут напрямую, минуя VPN. В других приложениях на время отключите VPN либо перейдите на Happ.',
  },
  {
    title: 'Пересоздали ключ',
    text: 'Старая ссылка больше не работает: удалите старую подписку в приложении и добавьте новую с этой страницы.',
  },
];
